import { useState } from 'react';

import { buildBrowserURL } from 'pkg/api/use_collection/utils';
import { replaceState } from 'pkg/router/state';

import type {
	CollectionSort,
	CollectionSortOrder,
	UseSortingOptions,
} from './use_sorting_types';

/**
 * Hook for managing sorting state with optional URL synchronization
 * @param options - Configuration options for sorting behavior
 * @returns CollectionSort object with current sort state and setSort function
 */
export function useSorting(options: UseSortingOptions = {}): CollectionSort {
	const {
		defaultSortBy = '',
		defaultSortByOrder = '',
		paramPrefix = '',
		stateInURL = false,
		onBeforeSort = null,
	} = options;

	const sortByParam = paramPrefix ? `${paramPrefix}_sort_by` : 'sort_by';
	const sortByOrderParam = paramPrefix
		? `${paramPrefix}_sort_by_order`
		: 'sort_by_order';

	const getInitialSortState = () => {
		let sortBy = defaultSortBy;
		let sortByOrder = defaultSortByOrder;

		if (stateInURL) {
			const qs = new URLSearchParams(window.location.search);
			if (qs.has(sortByParam)) {
				sortBy = qs.get(sortByParam) || defaultSortBy;
			}
			if (qs.has(sortByOrderParam)) {
				sortByOrder =
					(qs.get(sortByOrderParam) as CollectionSortOrder) ||
					defaultSortByOrder;
			}
		}

		return { sortBy, sortByOrder };
	};

	const initialState = getInitialSortState();
	const [sortBy, setSortBy] = useState<string>(initialState.sortBy);
	const [sortByOrder, setSortByOrder] = useState<CollectionSortOrder>(
		initialState.sortByOrder
	);

	const setSort = (column: string, order: CollectionSortOrder) => {
		if (onBeforeSort) {
			onBeforeSort();
		}

		setSortBy(column);
		setSortByOrder(order);

		if (stateInURL) {
			const qs = new URLSearchParams(window.location.search);

			if (column !== '') {
				qs.set(sortByParam, column);
			} else {
				qs.delete(sortByParam);
			}

			if (order !== '') {
				qs.set(sortByOrderParam, order);
			} else {
				qs.delete(sortByOrderParam);
			}

			replaceState(buildBrowserURL(qs));
		}
	};

	return {
		column: sortBy,
		order: sortByOrder,
		setSort,
	};
}
